import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import {
  AGENCY_STATUS_OPTION,
  COLOR_PAYMENT_METHOD,
  COLOR_STATUS_PAYMENT,
  getPlatFormImage,
  LIST_CURRENCY_OPTION,
  LIST_PAYMENT_METHOD_OPTION,
  LIST_REDEEM_STATUS_COLOR_OPTION,
  LIST_REDEEM_STATUS_OPTION,
  LIST_STATUS_PAYMENT_OPTION,
} from "helpers/constans";
import CountUp from "react-countup";
import { formatDateStringToKorea, formatISOString } from "helpers/format";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CollapseFilter from "components/Common/CollapseFilter";
import ModalMultiPreview from "components/Common/ModalMultiPreview";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import ModalPreview from "components/Common/ModalPreview";
import RedeemCodeForm from "./RedeemCodeForm";
import moment from "moment";
import DatePickerCustom from "components/Common/DatePickerCustom";
import CopyWrapper from "components/Common/CopyWrapper";
import { getAgencyList } from "store/agency/thunk";
import ModalPreviewQRCode from "components/Common/ModalPreviewQRCode";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import { useRole } from "components/Hooks/UserHooks";
import { ROUTES } from "routes/allRoutes";
import { push } from "helpers/navigation";
import { getLoggedInUserInfo } from "api/axiosClient";
import DropdownOption from "components/Common/DropdownOption";
import { getRedeemCodeList } from "store/redeemCode/thunk";
import redeemCodeApi from "api/redeemCodeApi";
import DropdownAsyncAgency from "components/Common/DropdownAsyncAgency";
import CoundownTime from "components/Common/CoundownTime";

interface Option {
  label: string;
  value: string;
}

const TYPE_SELECT_DEFAULT: string = "created_at";

registerLocale("en", en);
registerLocale("ko", ko);

const RedeemCodeIssue = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const data = getLoggedInUserInfo();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    page_size: withDefault(NumberParam, 30),
    keyword: withDefault(StringParam, ""),
    redeem_code: withDefault(StringParam, ""),
    status: withDefault(StringParam, ""),
    payment_status: withDefault(StringParam, ""),
    payment_method: withDefault(StringParam, ""),
    agency_id: withDefault(StringParam, ""),
    created_from: withDefault(StringParam, ""),
    created_to: withDefault(StringParam, ""),
    sort_by: withDefault(StringParam, "created_at"),
    order_by: withDefault(StringParam, "DESC"),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [linkImage, setLinkImage] = useState<any>(null);
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [previewQRCode, setPreviewQRCode] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [selectedAgency, setSelectedAgency] = useState<any>({
    label: t("All"),
    value: "all",
  });

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.created_from
      ? moment(query?.created_from || "", "Y-MM-DD").toDate()
      : null,
    query?.created_to
      ? moment(query?.created_to || "", "Y-MM-DD").toDate()
      : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [redeemCodeSearch, setRedeemCodeSearch] = useState<string>(
    query?.redeem_code || ""
  );

  const [paymentMethodSearch, setPaymentMethodSearch] = useState<string>(
    LIST_PAYMENT_METHOD_OPTION?.find(
      (item) => item?.value === query?.payment_method
    )?.value || ""
  );

  const [paymentStatusSearch, setPaymentStatusSearch] = useState<Option | any>(
    LIST_PAYMENT_METHOD_OPTION?.find(
      (item) => item?.value === query?.payment_status
    )?.value || ""
  );

  const [statusSearch, setStatusSearch] = useState<Option | any>(
    AGENCY_STATUS_OPTION?.find((item) => item?.value === query?.status)
      ?.value || ""
  );

  const [agencySearch, setAgencySearch] = useState<Option | any>("");

  const dispatch: any = useDispatch();
  const selectLayoutState = (state: any) => state.RedeemCode;
  const RedeemCodeProperties = createSelector(selectLayoutState, (state) => ({
    lstRedeemCode: state?.lstRedeemCode,
    isRedeemCodeLoading: state?.isRedeemCodeLoading,
    isRedeemCodeSuccess: state?.isRedeemCodeSuccess,
    error: state.error,
  }));

  const selectAgencyLayoutState = (state: any) => state.Agency;
  const AgencyProperties = createSelector(selectAgencyLayoutState, (state) => ({
    lstAgency: state.lstAgency,
    isAgencyLoading: state.isAgencyLoading,
    isAgencySuccess: state.isAgencySuccess,
    error: state.error,
  }));

  // Inside your component
  const { lstRedeemCode, isRedeemCodeLoading } =
    useSelector(RedeemCodeProperties);

  const { lstAgency } = useSelector(AgencyProperties);

  const AgencyList = useMemo(() => {
    const arr = lstAgency?.items?.map((i: any) => ({
      ...i,
      label: i?.name,
      value: i?.id + "",
    }));

    return arr || [];
  }, [lstAgency]);

  function loadTableData() {
    dispatch(getRedeemCodeList(query));
  }

  const searchData = () => {
    setQuery({
      ...query,
      redeem_code: redeemCodeSearch,
      status: statusSearch,
      payment_status: paymentStatusSearch,
      agency_id: agencySearch === "all" ? "" : agencySearch,
      payment_method: paymentMethodSearch,
      created_from: startDate ? formatISOString(startDate, "start") : "",
      created_to: endDate ? formatISOString(endDate, "end") : "",
      sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        redeem_code: "",
        status: "",
        payment_status: "",
        payment_method: "",
        agency_id: "",
        created_from: "",
        created_to: "",
        sort_by: TYPE_SELECT_DEFAULT,
        time_request: +new Date(),
      },
      "push"
    );

    setRedeemCodeSearch("");
    setAgencySearch("");
    setPaymentStatusSearch("");
    setPaymentMethodSearch("");
    setSelectedAgency({
      label: t("All"),
      value: "all",
    });
    setStatusSearch((_prev: any) => "");
    setDateSearch([null, null]);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        Cell: ({ ...props }) => {
          return (
            <div style={{ minWidth: "40px" }}>
              {lstRedeemCode?.pagination?.total -
                ((query?.page - 1) * query?.page_size + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 100,
        thClass: "text-center  whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.value;
          return (
            <div
              className="d-flex justify-content-center"
              style={{ minWidth: "100px" }}
            >
              {item !== "waiting" ? (
                <div
                  className={`text-center rounded-pill badge text-white bg-${
                    LIST_REDEEM_STATUS_OPTION.find((i) => item === i?.value)
                      ?.value
                  }`}
                  dir="ltr"
                >
                  {
                    LIST_REDEEM_STATUS_OPTION.find((i) => item === i?.value)
                      ?.label
                  }
                </div>
              ) : (
                <CoundownTime
                  expireDate={formatDateStringToKorea(
                    cell?.row?.original?.valid_to
                  )}
                  status={item}
                  type="push-log"
                  pushLogId={cell?.row?.original?.id}
                  triggerRefresh={triggerRefresh}
                />
              )}
            </div>
          );
        },
      },
      {
        Header: t("Redeem Code"),
        accessor: "code",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 120,
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Courses"),
        accessor: "courses",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 120,
        Cell: (cell: any) => {
          return cell?.value?.length
            ? cell?.value?.map((i: any) =>
                i?.name ? (
                  <div className="badge bg-primary">{i?.name}</div>
                ) : null
              )
            : "-";
        },
      },
      {
        Header: t("Agency Code"),
        accessor: "agency.code",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 120,
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <div className="cursor-pointer">
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Agency Name"),
        accessor: "agency.name",
        filterable: false,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <div className="cursor-pointer" style={{ minWidth: 150 }}>
                <CopyWrapper contentCopy={cell?.value}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Agency Payable Amount"),
        accessor: "amount",
        filterable: false,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return cell?.value ? (
            <div className="cursor-pointer" style={{ minWidth: 150 }}>
              {`${cell?.value}`}
            </div>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        Header: t("Currency"),
        accessor: "currency",
        filterable: false,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          let imgPlatform = getPlatFormImage(cell?.value ?? "");

          return cell?.value ? (
            <div className="d-flex align-items-center">
              {imgPlatform ? (
                <div style={{ width: "25px" }} className="me-1">
                  {imgPlatform ? (
                    <img
                      src={imgPlatform}
                      width={20}
                      height={20}
                      alt="platform"
                    />
                  ) : (
                    <span
                      className={`bg-secondary`}
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "3px",
                      }}
                    ></span>
                  )}
                </div>
              ) : (
                ""
              )}
              <div>
                {
                  LIST_CURRENCY_OPTION.find((i) => i.value === cell?.value)
                    ?.label
                }
              </div>
            </div>
          ) : (
            <div className="text-center">-</div>
          );
        },
      },
      {
        Header: t("Payment Status"),
        accessor: "payment_status",
        thWidth: 140,
        thClass: "text-center  whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.value;
          return item ? (
            <div
              className={`text-center rounded-pill badge text-white bg-${COLOR_STATUS_PAYMENT[item]}`}
              dir="ltr"
            >
              {LIST_STATUS_PAYMENT_OPTION.find((i) => item === i?.value)?.label}
            </div>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        Header: t("Payment Method"),
        accessor: "payment_method",
        thWidth: 140,
        thClass: "text-center  whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.value;
          return item ? (
            <div
              className={`text-center rounded-pill badge text-white bg-${COLOR_PAYMENT_METHOD[item]}`}
              dir="ltr"
            >
              {LIST_PAYMENT_METHOD_OPTION.find((i) => item === i?.value)?.label}
            </div>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        Header: t("ValidFrom"),
        accessor: "valid_from",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("ValidTo"),
        accessor: "valid_to",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ width: "150px", justifyContent: "center" }}
            >
              {!!item?.is_updatable && data?.user?.role === "admin" && (
                <React.Fragment>
                  <TooltipCustom
                    title={t("Button Update")}
                    id={`update-md-${item?.id}`}
                  >
                    <Link
                      className="btn btn-sm btn-soft-primary edit-item-btn"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpen(true);
                        setItemToEdit(item);
                      }}
                    >
                      <i className="ri-pencil-fill align-bottom "></i>
                    </Link>
                  </TooltipCustom>
                  <TooltipCustom
                    title={t("Button Delete")}
                    id={`delete-md-${item?.id}`}
                  >
                    <Link
                      className="btn btn-sm btn-soft-danger edit-item-btn"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setItemToDelete(item);
                      }}
                    >
                      <i className="ri-delete-bin-3-fill align-bottom"></i>
                    </Link>
                  </TooltipCustom>
                </React.Fragment>
              )}

              {data?.user?.role === "agency" && (
                <TooltipCustom
                  title={t("View Detail")}
                  id={`view-md-${item?.id}`}
                >
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(true);
                      setItemToEdit(item);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom "></i>
                  </Link>
                </TooltipCustom>
              )}
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, lstRedeemCode?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  // Begin::Delete
  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await redeemCodeApi?.deleteRedeemCode(id);
      if (response?.statusCode === 200 && response?.message === "Success") {
        setItemToDelete(false);
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  const onCloseQRCodeClick = () => {
    setPreviewQRCode(null);
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    loadTableData();
  };

  const getSummary = async () => {
    try {
      const response: any = await redeemCodeApi.getRedeemCodeSumary(query);
      setSummary(response?.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  useEffect(() => {
    document.title = `${t("Redeem Code Management")} | Picaverse Agency Star+`;
    document.body.classList.remove("vertical-sidebar-enable");
    dispatch(getAgencyList(query));
    getSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Redeem Code Issue")} pageTitle={t("Home")} />
          {
            <Row>
              <Col lg={12}>
                <Card
                  id="customerList"
                  style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                >
                  <CardHeader className="border-0">
                    <div className="d-block d-xl-flex justify-content-between align-items-start">
                      <div
                        className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                        style={{ maxWidth: "100%" }}
                      >
                        <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center ">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                  {t("Total")}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                              <div>
                                <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                  <span className="counter-value text-primary">
                                    <CountUp
                                      start={0}
                                      end={summary?.total || 0}
                                      duration={1}
                                    />
                                  </span>
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>

                        <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center ">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                  {t("Total Waiting")}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                              <div>
                                <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                  <span className="counter-value text-primary">
                                    <CountUp
                                      start={0}
                                      end={summary?.waiting || 0}
                                      duration={1}
                                    />
                                  </span>
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>

                        <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center ">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                  {t("Total Used")}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                              <div>
                                <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                  <span className="counter-value text-primary">
                                    <CountUp
                                      start={0}
                                      end={summary?.used || 0}
                                      duration={1}
                                    />
                                  </span>
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>

                        <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center ">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                  {t("Total Expired")}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                              <div>
                                <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                  <span className="counter-value text-primary">
                                    <CountUp
                                      start={0}
                                      end={summary?.expired || 0}
                                      duration={1}
                                    />
                                  </span>
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                        <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center ">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                  {t("Total Cancel")}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                              <div>
                                <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                  <span className="counter-value text-primary">
                                    <CountUp
                                      start={0}
                                      end={summary?.canceled || 0}
                                      duration={1}
                                    />
                                  </span>
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </div>

                      {data?.user?.role === "admin" && (
                        <div className="col-sm-auto">
                          <button
                            type="button"
                            className="btn btn-success d-none d-md-block"
                            onClick={() => {
                              setIsOpen(true);
                              setItemToEdit(null);
                            }}
                          >
                            <i className="ri-add-fill align-bottom me-1"></i>{" "}
                            {t("Button Create Redeem Code")}
                          </button>
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                    <CollapseFilter>
                      <Row className="g-4 align-items-center mt-0 mt-md-2">
                        <Col
                          sm={12}
                          md={3}
                          xl={3}
                          xxl={3}
                          className="mt-3 mt-md-2"
                        >
                          <LabelWrapper
                            label={`${t("Redeem Code")}`}
                            isShow={!!redeemCodeSearch}
                          >
                            <Input
                              type="text"
                              className="form-control search"
                              placeholder={`${t("Redeem Code")}...`}
                              value={redeemCodeSearch}
                              onChange={(e) =>
                                setRedeemCodeSearch(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  searchData();
                                }
                              }}
                            />
                          </LabelWrapper>
                        </Col>

                        {data?.user?.role === "admin" && (
                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper label={t("Agency")} isShow={true}>
                              <DropdownAsyncAgency
                                name="agency"
                                onChangeSelect={(event: any) => {
                                  setAgencySearch(event?.value);
                                  setSelectedAgency(event);
                                }}
                                isMulti={false}
                                placeholder={`${t("Select Agency")}...`}
                                isShowIconCheck={false}
                                listCheckedUsers={selectedAgency}
                                isAllOption={true}
                                isNotBelongToAgency={false}
                                isClearable={false}
                              />
                            </LabelWrapper>
                          </Col>
                        )}

                        <Col
                          sm={12}
                          md={3}
                          xl={3}
                          xxl={3}
                          className="mt-3 mt-md-3"
                        >
                          <LabelWrapper
                            label={t("Redeem Status")}
                            isShow={true}
                          >
                            {/* <DropdownOption
                              name="status"
                              dataList={LIST_REDEEM_STATUS_OPTION}
                              placeholder={`${t("Select Agency")}...`}
                              className="dropdown-status-rounded"
                              classNamePrefix="name-prefix"
                              initialValue={
                                LIST_REDEEM_STATUS_OPTION.find(
                                  (i) => i.value === statusSearch
                                ) || { label: t("All"), value: "" }
                              }
                              onChangeSelect={(e: any) => {
                                setStatusSearch(e?.value);
                              }}
                              isHasOptionAll={true}
                              optionAll={{ label: t("All"), value: "" }}
                            /> */}
                            <DropdownStatusNew
                              name="status"
                              dataList={LIST_REDEEM_STATUS_OPTION || []}
                              placeholder={`${t("Select Status")}...`}
                              className="dropdown-status-rounded"
                              classNamePrefix="name-prefix"
                              initialValue={statusSearch}
                              onChangeSelect={(e: any) =>
                                setStatusSearch(e?.value)
                              }
                              isHasOptionAll={true}
                              optionAll={{ label: t("All Filter"), value: "" }}
                              colors={LIST_REDEEM_STATUS_COLOR_OPTION}
                            />
                          </LabelWrapper>
                        </Col>

                        {data?.user?.role === "admin" && (
                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper
                              label={t("Payment Status")}
                              isShow={true}
                            >
                              <DropdownStatusNew
                                name="course_status"
                                dataList={LIST_STATUS_PAYMENT_OPTION || []}
                                placeholder={`${t("Select Status")}...`}
                                className="dropdown-status-rounded"
                                classNamePrefix="name-prefix"
                                initialValue={paymentStatusSearch}
                                onChangeSelect={(e: any) =>
                                  setPaymentStatusSearch(e?.value)
                                }
                                isHasOptionAll={true}
                                optionAll={{
                                  label: t("All Filter"),
                                  value: "",
                                }}
                                colors={COLOR_STATUS_PAYMENT}
                              />
                            </LabelWrapper>
                          </Col>
                        )}

                        {data?.user?.role === "admin" && (
                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper
                              label={t("Payment Method")}
                              isShow={true}
                            >
                              <DropdownStatusNew
                                name="method"
                                dataList={LIST_PAYMENT_METHOD_OPTION || []}
                                placeholder={`${t("No data")}`}
                                className="dropdown-status-rounded"
                                classNamePrefix="name-prefix"
                                initialValue={paymentMethodSearch}
                                onChangeSelect={(event: any) =>
                                  setPaymentMethodSearch(event?.value)
                                }
                                colors={COLOR_PAYMENT_METHOD}
                                isHasOptionAll={true}
                                optionAll={{
                                  label: t("All Filter"),
                                  value: "",
                                }}
                              />
                            </LabelWrapper>
                          </Col>
                        )}

                        <Col
                          sm={12}
                          md={3}
                          xl={3}
                          xxl={3}
                          className="mt-3 mt-md-3 date-picker-wrapper-custom"
                        >
                          <LabelWrapper
                            label={t("Date Range")}
                            isShow={!!startDate || !!endDate}
                          >
                            <DatePickerCustom
                              startDate={startDate || null}
                              endDate={endDate || null}
                              onChangePicker={handleChangePicker}
                            />
                          </LabelWrapper>
                        </Col>

                        <Col
                          sm={12}
                          md={12}
                          xl={12}
                          xxl={12}
                          className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-3 mt-md-3"
                        >
                          <div>
                            <button
                              type="button"
                              className="btn btn-primary me-1"
                              onClick={searchData}
                            >
                              <i className="ri-search-line align-bottom me-1"></i>{" "}
                              {t("Button Search")}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary fs-14"
                              onClick={resetData}
                            >
                              <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                              {t("Button Reset")}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </CollapseFilter>
                  </div>
                  <CardBody className="pt-3">
                    <div>
                      <TableContainer
                        className="custom-header-css"
                        divClass="table-responsive table-card"
                        tableClass="align-middle table-bordered-dashed"
                        theadClass="table-light text-muted"
                        columns={columns}
                        data={
                          lstRedeemCode?.items?.length
                            ? lstRedeemCode?.items
                            : []
                        }
                        customPageSize={query.page_size}
                        customPageIndex={query.page - 1}
                        totalRecords={lstRedeemCode?.pagination?.total}
                        customPageCount={Math.ceil(
                          Number(lstRedeemCode?.pagination?.total) /
                            Number(query.page_size)
                        )}
                        handleChangePage={handleChangePage}
                        manualSorting={true}
                        sorting={{
                          sort_by: query.sort_by,
                          order_by: query.order_by,
                        }}
                        handleChangeSorting={handleChangeSorting}
                        isLoading={isRedeemCodeLoading}
                      />
                    </div>
                    <ToastContainer closeButton={false} limit={1} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          }
        </Container>

        {isOpen && (
          <RedeemCodeForm
            setIsOpen={setIsOpen}
            id={itemToEdit?.id}
            isShowDetail={data?.user?.role === "agency"}
            agencyList={AgencyList}
            setItemToEdit={setItemToEdit}
            triggerRefresh={triggerRefresh}
            setLinkImage={setLinkImage}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Redeem Code Information")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        {previewQRCode && (
          <ModalPreviewQRCode
            data={previewQRCode?.branch_link}
            title={previewQRCode?.title}
            onCloseQRCodeClick={onCloseQRCodeClick}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default RedeemCodeIssue;
